<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ConversationsAnalysisAiApi from 'dashboard/api/conversationsAnalysisAi';
import Modal from 'dashboard/components/Modal.vue';
import ModalHeader from 'dashboard/components/ModalHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['created']);

const show = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');

const name = ref('');
const isSubmitting = ref(false);

const filters = ref({
  start_date: '',
  end_date: '',
  inbox_id: '',
  status: '',
  assignee_id: '',
  label: '',
});

const analysisType = ref('conversations');
const customPrompt = ref('');

const statusOptions = computed(() => [
  { value: '', label: t('REPORT.AI.FILTERS.STATUS.ALL') },
  { value: 'open', label: t('REPORT.AI.FILTERS.STATUS.OPEN') },
  { value: 'resolved', label: t('REPORT.AI.FILTERS.STATUS.RESOLVED') },
  { value: 'pending', label: t('REPORT.AI.FILTERS.STATUS.PENDING') },
  { value: 'snoozed', label: t('REPORT.AI.FILTERS.STATUS.SNOOZED') },
]);

const analysisTypeOptions = computed(() => [
  {
    value: 'conversations',
    label: t('REPORT.AI.ANALYSIS_TYPE.CONVERSATIONS'),
  },
  { value: 'performance', label: t('REPORT.AI.ANALYSIS_TYPE.PERFORMANCE') },
  { value: 'time', label: t('REPORT.AI.ANALYSIS_TYPE.TIME') },
  { value: 'custom', label: t('REPORT.AI.ANALYSIS_TYPE.CUSTOM') },
]);

const isCustom = computed(() => analysisType.value === 'custom');

const isSubmitDisabled = computed(
  () =>
    isSubmitting.value ||
    !name.value.trim() ||
    (isCustom.value && !customPrompt.value.trim())
);

const payload = computed(() => {
  const appliedFilters = {};
  Object.entries(filters.value).forEach(([key, value]) => {
    if (value !== '') appliedFilters[key] = value;
  });

  const result = {
    name: name.value.trim(),
    type: analysisType.value,
    filters: appliedFilters,
  };
  if (isCustom.value) result.prompt = customPrompt.value.trim();
  return result;
});

const resetForm = () => {
  name.value = '';
  filters.value = {
    start_date: '',
    end_date: '',
    inbox_id: '',
    status: '',
    assignee_id: '',
    label: '',
  };
  analysisType.value = 'conversations';
  customPrompt.value = '';
};

const handleClose = () => {
  show.value = false;
};

const handleAnalyze = async () => {
  isSubmitting.value = true;
  try {
    await ConversationsAnalysisAiApi.create(payload.value);
    useAlert(t('REPORT.AI.CREATE.SUCCESS'));
    emit('created');
    resetForm();
    handleClose();
  } catch {
    useAlert(t('REPORT.AI.CREATE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="show" :on-close="handleClose">
    <ModalHeader
      :header-title="t('REPORT.AI.MODAL.TITLE')"
      :header-content="t('REPORT.AI.MODAL.DESCRIPTION')"
    />

    <div class="modal-content">
      <Input
        v-model="name"
        is-star
        :label="t('REPORT.AI.NAME.LABEL')"
        :placeholder="t('REPORT.AI.NAME.PLACEHOLDER')"
      />

      <div class="grid grid-cols-2 gap-4 mt-4">
        <Input
          v-model="filters.start_date"
          type="date"
          :label="t('REPORT.AI.FILTERS.START_DATE')"
        />
        <Input
          v-model="filters.end_date"
          type="date"
          :label="t('REPORT.AI.FILTERS.END_DATE')"
        />
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('REPORT.AI.FILTERS.INBOX') }}
        </label>
        <select
          v-model="filters.inbox_id"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option value="">
            {{ t('REPORT.AI.FILTERS.INBOX_ALL') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('REPORT.AI.FILTERS.STATUS.LABEL') }}
        </label>
        <select
          v-model="filters.status"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option
            v-for="opt in statusOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('REPORT.AI.FILTERS.AGENT') }}
        </label>
        <select
          v-model="filters.assignee_id"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option value="">
            {{ t('REPORT.AI.FILTERS.AGENT_ALL') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('REPORT.AI.FILTERS.LABEL') }}
        </label>
        <select
          v-model="filters.label"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option value="">
            {{ t('REPORT.AI.FILTERS.LABEL_ALL') }}
          </option>
          <option
            v-for="label_ in labels"
            :key="label_.id"
            :value="label_.title"
          >
            {{ label_.title }}
          </option>
        </select>
      </div>

      <div class="mt-4">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('REPORT.AI.ANALYSIS_TYPE.LABEL') }}
        </label>
        <select
          v-model="analysisType"
          class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        >
          <option
            v-for="opt in analysisTypeOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div v-if="isCustom" class="mt-4">
        <TextArea
          v-model="customPrompt"
          :label="t('REPORT.AI.CUSTOM_PROMPT.LABEL')"
          :placeholder="t('REPORT.AI.CUSTOM_PROMPT.PLACEHOLDER')"
        />
      </div>

      <div class="flex justify-end mt-6">
        <Button
          :label="t('REPORT.AI.BUTTON')"
          :disabled="isSubmitDisabled"
          :is-loading="isSubmitting"
          @click="handleAnalyze"
        />
      </div>
    </div>
  </Modal>
</template>
